<script setup lang="ts">
    import { computed, toRef } from 'vue'
    import { strCompareLowerCase } from '@common/Common'

    import type {
        ProjectUsersTagsManagerViewModel,
        TagVM
    } from '@Projects/Components/ProjectUsersTagsManager'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: ProjectUsersTagsManagerViewModel
    }>()

    const emit = defineEmits<{
        (e: 'create-tag', tag: string | null): void
        (e: 'delete-tag', tag: string): void
        (e: 'update-tag', oldTag: string, newTag: string | null): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- derived ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const tagsView = computed<TagVM[]>(() =>
        [...(vm.value.tags ?? [])].sort((a, b) =>
            strCompareLowerCase(a.tag, b.tag)
        )
    )

    const createError = computed(() => vm.value.createError)
    const updateError = computed(() => vm.value.updateError)

    // ---------------- methods ----------------

    function handleCreateProjectUsersTag()
    {
        vm.value.createError = null
        vm.value.updateError = null

        emit('create-tag', vm.value.newTag)
    }

    function handleDeleteProjectUsersTag(tag: string)
    {
        emit('delete-tag', tag)
    }

    function handleUpdateProjectUsersTag(tagVm: TagVM)
    {
        vm.value.createError = null
        vm.value.updateError = null

        emit('update-tag', tagVm.tag, tagVm.renamedTag)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Manage people tags</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div class="input-group">
                            <input type="text" v-model.trim="vm.newTag" class="form-control" placeholder="new tag...">
                            <div class="input-group-append">
                                <button class="btn btn-outline-secondary" type="button" :disabled="!vm.newTag" @click="handleCreateProjectUsersTag">Create tag</button>
                            </div>
                        </div>
                        <div class="error">{{vm.createError}}</div>

                        <table class="table table-hover mt-4">
                            <tbody>
                                <tr v-for="t in tagsView" :key="t.tag">
                                    <td>
                                        <span v-if="!t.editing" class="editable editable-secondary" @click="t.edit()">{{t.tag}}</span>
                                        <input v-if="t.editing" type="text" v-model.trim="t.renamedTag" class="form-control" />
                                        <div v-if="t.editing && updateError" class="error">{{updateError}}</div>
                                    </td>

                                    <td class="text-right" style="width: 100px;">
                                        <div v-if="!t.editing">
                                            <button type="button" @click="handleDeleteProjectUsersTag(t.tag)" class="btn btn-outline-secondary btn-sm" title="delete">
                                                <i class="fa fa-trash-can"></i>
                                            </button>
                                        </div>

                                        <div v-if="t.editing">
                                            <button type="button" @click="handleUpdateProjectUsersTag(t)" class="btn btn-primary btn-sm" title="confirm" :disabled="!t.canSave">
                                                <span class="fa fa-check"></span>
                                            </button>

                                            <button type="button" @click="t.cancelEdit()" class="btn btn-outline-secondary btn-sm" title="cancel">
                                                <span class="fa fa-times"></span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>

                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>